/*----------------------------------
- COMPONENT: PUBLIC DOMAINS ROW CARDS
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 * @adr ADR-0028
 *
 * The phone presentation of the bounded public preview.
 *
 * Same rows, same order, same cap as the table: this component receives the rows the page has
 * already resolved and draws them, so there is no second query, no pagination and no client-side
 * ordering. The handoff block under them is the table's See all control in the shape the phone
 * mockup gives it, with the same handler and the same test id, so the conversion path is one path.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import CtaPill from '@uniquedomains/components/website/WebsiteCta';
import { PUBLIC_PREVIEW_HANDOFF_COPY } from '@/client/catalogs/landing/domains/publicPreviewCopy';

import PublicDomainsRowCard from './PublicDomainsRowCard';

// Types
import type { PublicDomainsTableColumn } from '@/client/catalogs/landing/domains/publicTableColumns';
import type { PublicDomainsTableRow } from '../table/PublicDomainsTable';
import { BROWSE_ESTIMATE_SPOKEN_LABEL } from '@uniquedomains/components/radar/catalogs/browseResultCountCopy';
import type { DatabaseBrowseCountPrecision } from '@uniquedomains/components/contracts/domains/browseFacets';

import '../table/publicDomainsTable.css';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/**
 * The columns the card head already draws, in its own geometry.
 *
 * Everything else in the catalog becomes a band, so the card cannot silently drop a column the
 * table gained.
 */
const CARD_HEAD_COLUMN_IDS: readonly string[] = ['acquisition', 'askPrice', 'renewal', 'verdict', 'signals'];

/** Ghost rows behind the handoff block: decoration for the rows the preview stops at. */
const GHOST_ROW_KEYS = ['a', 'b', 'c'] as const;

/*----------------------------------
- TYPES
----------------------------------*/

type PublicDomainsRowCardsProps = {
    rows: readonly PublicDomainsTableRow[];
    columns: readonly PublicDomainsTableColumn[];
    totalCountLabel: string | null;
    /** Adds the spoken "(estimated)" after a sampled total (ADR-0034 section 5). */
    countPrecision: DatabaseBrowseCountPrecision;
    /** Every gated affordance hands off through the same conversion path the table uses. */
    onOpenRow: (domain: string) => void;
    onSeeAll: () => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PublicDomainsRowCards = ({
    rows,
    columns,
    totalCountLabel,
    countPrecision,
    onOpenRow,
    onSeeAll,
}: PublicDomainsRowCardsProps) => {
    const bandColumns = columns.filter((column) => !CARD_HEAD_COLUMN_IDS.includes(column.id));
    const rowCount = rows.length.toLocaleString('en-US');

    return (
        <div data-reveal>
            <p class="m-0 px-0.5 pb-3 text-[13px] font-semibold tabular-nums text-[#4a4d4e]">
                Showing <b>{rowCount}</b>
                {totalCountLabel ? (
                    <>
                        {' '}
                        of <b>{totalCountLabel}</b>
                        {countPrecision === 'sampled' ? <span class="sr-only"> {BROWSE_ESTIMATE_SPOKEN_LABEL}</span> : null}
                    </>
                ) : null}
            </p>

            <div class="flex flex-col gap-2">
                {rows.map(({ key, row, projection }) => (
                    <PublicDomainsRowCard
                        key={key}
                        row={row}
                        projection={projection}
                        bandColumns={bandColumns}
                        onOpen={onOpenRow}
                    />
                ))}
            </div>

            <div class="ud-public-row-cards__handoff">
                <span class="absolute inset-0 flex flex-col gap-2" aria-hidden="true">
                    {GHOST_ROW_KEYS.map((ghostKey) => (
                        <span
                            key={ghostKey}
                            class="h-[70px] flex-none rounded-[20px] border border-[#ebe7de] bg-white"
                        />
                    ))}
                </span>

                <div class="relative rounded-[18px] bg-[#1b1d1e] px-[18px] pb-4 pt-[18px] text-white shadow-[0_18px_44px_rgba(27,29,30,0.22)]">
                    <p class="m-0 text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-white/50">
                        {PUBLIC_PREVIEW_HANDOFF_COPY.eyebrow.replace('{count}', rowCount)}
                    </p>
                    <p class="mb-0 mt-2 text-[19px] font-semibold leading-[1.25] tracking-[-0.02em]">
                        {totalCountLabel
                            ? PUBLIC_PREVIEW_HANDOFF_COPY.title.replace('{total}', totalCountLabel)
                            : PUBLIC_PREVIEW_HANDOFF_COPY.titleFallback}
                    </p>
                    <p class="mb-0 mt-2 text-[13.5px] leading-[1.55] text-white/[0.68]">
                        {PUBLIC_PREVIEW_HANDOFF_COPY.body}
                    </p>
                    <CtaPill
                        variant="accent-inverted"
                        size="lg"
                        width="full"
                        class="mt-3.5"
                        data-testid="domains-see-all"
                        onClick={onSeeAll}
                    >
                        {PUBLIC_PREVIEW_HANDOFF_COPY.cta}
                    </CtaPill>
                </div>
            </div>
        </div>
    );
};

export default PublicDomainsRowCards;
